import { Children, ReactNode, useState } from 'react';
import { MoreVertOutlined } from '@mui/icons-material';
import { Box, ClickAwayListener, IconButton, Paper, Tooltip } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { customShadow } from '@juno/ui';

interface CollapsibleMenuProps {
  children: ReactNode;
  maxVisibleIcons?: number;
}

const CollapsibleMenu: React.FC<CollapsibleMenuProps> = ({ children, maxVisibleIcons = 3 }) => {
  const [expanded, setExpanded] = useState(false);

  const handleToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    setExpanded((prev) => !prev);
  };

  const childrenArray = Children.toArray(children);
  const hasMoreIcons = childrenArray.length > maxVisibleIcons;

  return (
    <Box>
      {!expanded ? (
        <Box display='flex' alignItems='center'>
          {childrenArray.slice(0, maxVisibleIcons)}
          {hasMoreIcons && (
            <Tooltip title='More Options' arrow>
              <IconButton onClick={handleToggle}>
                <MoreVertOutlined />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ) : (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            style={{ zIndex: 900 }}
          >
            <ClickAwayListener
              onClickAway={(e) => {
                setExpanded(false);
              }}
            >
              <Paper
                elevation={3}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: 2,
                  padding: '1px 2px',
                  boxShadow: customShadow,
                }}
                onClick={() => setExpanded(false)}
              >
                {childrenArray}
                <IconButton onClick={handleToggle}>
                  <MoreVertOutlined />
                </IconButton>
              </Paper>
            </ClickAwayListener>
          </motion.div>
        </AnimatePresence>
      )}
    </Box>
  );
};

export default CollapsibleMenu;
